import { useCallback, useEffect, useState } from 'react'
import Icon, { type IconName } from '../components/Icon'
import { worx, type Application } from './client'
import { Logo } from './Jobs'
import { ago, tw } from './strings'

/** Over. Not "failed" — hired is an ending too, and the best one. */
const FINISHED = ['hired', 'not_selected', 'withdrawn']

/** The event line's picture. One per stage, and the stage is the only input. */
const EVENT_ICONS: Record<string, IconName> = {
  applied: 'clock',
  viewed: 'eye',
  contacted: 'chat',
  interview: 'calendar',
  hired: 'star',
  not_selected: 'info',
  withdrawn: 'close',
}

/**
 * Where a worker sees what happened to everything they sent.
 *
 * ── The status is the whole screen ──────────────────────────────────────────
 *
 * On the other side of this app a status change is a dropdown; here it is the
 * answer to "did anyone read it", which is the question this app exists to
 * stop going unanswered. So each card says it three times over, in the three
 * ways people actually ask it: a pill for the stage, a line for what happened
 * last, and a tracker for how far along it is.
 *
 * ── Active and Past ─────────────────────────────────────────────────────────
 *
 * Finished applications do not leave the screen — an application that vanishes
 * teaches people the app lost it — but they stop competing with the live ones
 * for attention. Hired sits in Past with the others, because it is an ending;
 * the pill is what tells you which kind.
 */
export default function Applications({
  onOpenThread,
  onOpenJob,
}: {
  onOpenThread: (threadId: string) => void
  onOpenJob?: (jobId: string) => void
}) {
  const [rows, setRows] = useState<Application[] | null>(null)
  const [tab, setTab] = useState<'active' | 'past'>('active')
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setRows((await worx.applications()).applications)
    } catch {
      setRows([])
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function withdraw(id: string) {
    setBusy(id)
    try {
      await worx.withdraw(id)
      await load()
    } finally {
      setBusy(null)
    }
  }

  if (!rows) return <p className="muted">…</p>

  const active = rows.filter((a) => !FINISHED.includes(a.status))
  const past = rows.filter((a) => FINISHED.includes(a.status))
  const shown = tab === 'active' ? active : past

  return (
    <div className="w-feed">
      <h1 className="w-feed-heading">{tw('w.apps.title')}</h1>

      {/* Two segments in one track, not two chips: these are the two halves of
          one list, and the track is what says so. */}
      <div className="w-seg" role="tablist">
        <button
          role="tab"
          className={tab === 'active' ? 'w-seg-b on' : 'w-seg-b'}
          aria-selected={tab === 'active'}
          onClick={() => setTab('active')}
        >
          {tw('w.apps.active')} <span className="w-seg-n">{active.length}</span>
        </button>
        <button
          role="tab"
          className={tab === 'past' ? 'w-seg-b on' : 'w-seg-b'}
          aria-selected={tab === 'past'}
          onClick={() => setTab('past')}
        >
          {tw('w.apps.past')} <span className="w-seg-n">{past.length}</span>
        </button>
      </div>

      {/* Where the messages will arrive, said once, on the screen somebody is
          on while they are waiting for one. */}
      {tab === 'active' && (
        <p className="w-note">
          <span className="w-note-ic"><Icon name="chat" size={18} /></span>
          {tw('w.apps.banner')}
        </p>
      )}

      {shown.length === 0 && (
        <p className="muted w-empty">{tab === 'active' ? tw('w.apps.noneActive') : tw('w.apps.nonePast')}</p>
      )}

      {shown.map((a) => (
        <ApplicationCard
          key={a.id}
          app={a}
          busy={busy === a.id}
          onOpenThread={() => a.threadId && onOpenThread(a.threadId)}
          onOpenJob={() => onOpenJob?.(a.jobId)}
          onWithdraw={() => void withdraw(a.id)}
        />
      ))}
    </div>
  )
}

function ApplicationCard({
  app,
  busy,
  onOpenThread,
  onOpenJob,
  onWithdraw,
}: {
  app: Application
  busy: boolean
  onOpenThread: () => void
  onOpenJob: () => void
  onWithdraw: () => void
}) {
  const live = !FINISHED.includes(app.status)
  /*
   * When the last thing happened.
   *
   * `updatedAt` for anything the employer did, `viewedAt` for the one case
   * where the two disagree — a status can move again afterwards, and "viewed
   * yesterday" should not silently become "viewed an hour ago" because
   * somebody typed a note.
   */
  const when = app.status === 'viewed' && app.viewedAt ? app.viewedAt : app.updatedAt

  return (
    <article className="w-appcard">
      <Logo name={app.employerName} src={app.employerLogoId} className="w-appcard-logo" />

      <div className="w-appcard-main">
        <div className="w-appcard-top">
          <h3 className="w-appcard-title">{app.title}</h3>
          {app.isDemo && <span className="w-stat">{tw('w.demo.badge')}</span>}
          <span className={`w-stat w-stat-${app.status}`}>
            <span className="w-stat-dot" aria-hidden="true" />
            {tw(`w.status.${app.status}` as never)}
          </span>
        </div>
        <p className="w-appcard-emp">{app.employerName}</p>
        <p className="w-appcard-when">{tw('w.apps.submitted')} {ago(app.createdAt)}</p>

        <p className="w-appcard-ev">
          <span className="w-cfact-ic"><Icon name={EVENT_ICONS[app.status] ?? 'info'} size={16} /></span>
          <span>
            {tw(`w.apps.ev.${app.status}` as never)}
            {(app.status === 'viewed' || app.status === 'interview') && ` ${ago(when)}`}
          </span>
        </p>
      </div>

      <div className="w-appcard-foot">
        <Steps status={app.status} />

        <div className="w-appcard-do">
          {app.threadId ? (
            <button className="w-cta" onClick={onOpenThread}>
              {app.status === 'contacted' ? tw('w.apps.viewUpdate') : tw('w.apps.openConversation')}
              <Icon name="chevron" size={18} />
            </button>
          ) : (
            /* Outlined, because there is nothing waiting: looking at what you
               sent is not the same invitation as a conversation that has
               already started. */
            <button className="w-cta w-cta-quiet" onClick={onOpenJob}>
              {tw('w.apps.viewApplication')} <Icon name="chevron" size={18} />
            </button>
          )}
          {live && (
            <button className="link w-appcard-withdraw" onClick={onWithdraw} disabled={busy}>
              {tw('w.applications.withdraw')}
            </button>
          )}
        </div>
      </div>
    </article>
  )
}

/**
 * How far along, in three steps.
 *
 * ── Why the third step is not always "Interview" ────────────────────────────
 *
 * The stages are applied → viewed → contacted → interview → hired, which is
 * five, and five nodes on a phone is a row of dots nobody can read. So the
 * tracker shows the two that always happen and then WHERE YOU ARE: the third
 * node takes the name of the current stage once it is past viewed, and says
 * "Interview" as the greyed-out future when it is not. Nothing is ever
 * claimed — a hollow node with a dashed line is the honest drawing of "this
 * has not happened".
 */
function Steps({ status }: { status: string }) {
  const order = ['applied', 'viewed', 'contacted', 'interview', 'hired']
  const at = order.indexOf(status)
  // Withdrawn and not_selected keep whatever they reached: the tracker is a
  // record of what happened, and an ending does not erase the two steps that
  // did happen.
  const reached = at < 0 ? 1 : at + 1
  const third = at >= 2 ? status : 'interview'

  const nodes: { key: string; label: string; done: boolean }[] = [
    { key: 'applied', label: tw('w.status.applied'), done: true },
    { key: 'viewed', label: tw('w.status.viewed'), done: reached >= 2 },
    { key: third, label: tw(`w.status.${third}` as never), done: at >= 2 },
  ]

  return (
    <ol className="w-steps" aria-label={tw('w.apps.title')}>
      {nodes.map((n, i) => (
        <li key={n.key} className={n.done ? 'w-step on' : 'w-step'}>
          {i > 0 && <span className={n.done ? 'w-step-line on' : 'w-step-line'} aria-hidden="true" />}
          <span className="w-step-dot">{n.done && <Icon name="check" size={13} />}</span>
          <span className="w-step-label">{n.label}</span>
        </li>
      ))}
    </ol>
  )
}
